<template>
  <div v-if="hasTracking" class="mt-3 rounded-lg border border-purple-100 bg-purple-50/80 overflow-hidden">
    <!-- 摘要头 -->
    <div class="px-3 py-2 bg-purple-100/60 border-b border-purple-100 flex items-center justify-between">
      <span class="text-xs font-semibold text-purple-900">打开追踪</span>
      <span :class="badgeClass" class="px-2 py-0.5 text-xs rounded font-medium">
        {{ badgeLabel }}
      </span>
    </div>

  <div class="px-3 py-2 space-y-2">
      <!-- 统计行 -->
      <div class="grid grid-cols-3 gap-2 text-xs">
        <div class="bg-white rounded px-2 py-1.5 border border-purple-50">
          <div class="text-gray-400 mb-0.5">发送</div>
          <div class="font-semibold text-gray-800">{{ email.outbound_reply_count || 0 }} 次</div>
        </div>
        <div class="bg-white rounded px-2 py-1.5 border border-purple-50">
          <div class="text-gray-400 mb-0.5">打开</div>
          <div class="font-semibold text-gray-800">{{ email.outbound_open_count || 0 }} 次</div>
        </div>
        <div class="bg-white rounded px-2 py-1.5 border border-purple-50">
          <div class="text-gray-400 mb-0.5">首次打开</div>
          <div class="font-medium text-gray-700 truncate">
            {{ email.outbound_first_opened_at ? formatDateTime(email.outbound_first_opened_at) : '—' }}
          </div>
        </div>
      </div>

      <!-- 首次打开环境信息 -->
      <div
        v-if="email.outbound_is_opened && hasOpenInfo"
        class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs"
      >
        <div class="bg-white rounded px-2 py-1.5 border border-purple-50">
          <div class="text-gray-400 mb-0.5">设备</div>
          <div class="font-medium text-gray-800 flex items-center gap-1">
            <span>{{ getDeviceIcon(openInfo.device_type) }}</span>
            <span>{{ getDeviceLabel(openInfo.device_type) || '—' }}</span>
          </div>
        </div>
        <div class="bg-white rounded px-2 py-1.5 border border-purple-50">
          <div class="text-gray-400 mb-0.5">IP</div>
          <div class="font-medium text-gray-800 truncate" :title="openInfo.ip_address || ''">
            {{ openInfo.ip_address || '—' }}
          </div>
        </div>
        <div class="bg-white rounded px-2 py-1.5 border border-purple-50 col-span-2 sm:col-span-1">
          <div class="text-gray-400 mb-0.5">地区</div>
          <div class="font-medium text-gray-800 truncate" :title="formatLocation(openInfo)">
            {{ formatLocation(openInfo) || '—' }}
          </div>
        </div>
        <div
          v-if="openInfo.os || openInfo.browser"
          class="bg-white rounded px-2 py-1.5 border border-purple-50 col-span-2 sm:col-span-1"
        >
          <div class="text-gray-400 mb-0.5">系统 / 浏览器</div>
          <div class="font-medium text-gray-700 truncate">
            {{ [openInfo.os, openInfo.browser].filter(Boolean).join(' · ') }}
          </div>
        </div>
      </div>

      <!-- 各次发送明细 -->
      <div v-if="replies.length > 0" class="space-y-2 pt-1">
        <div class="text-xs text-purple-700 font-medium">发送记录</div>
        <div
          v-for="reply in replies"
          :key="reply.id"
          class="bg-white rounded border border-purple-50 overflow-hidden"
        >
          <div class="px-2.5 py-2 border-b border-purple-50 flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <div class="text-xs font-medium text-gray-800 truncate">{{ reply.subject || '(无主题)' }}</div>
              <div class="text-xs text-gray-500 truncate mt-0.5">{{ (reply.to_emails || []).join(', ') }}</div>
            </div>
            <span
              :class="reply.open_count > 0 ? 'text-purple-700 bg-purple-50' : 'text-gray-500 bg-gray-50'"
              class="flex-shrink-0 px-2 py-0.5 text-xs rounded whitespace-nowrap"
            >
              {{ reply.open_count > 0 ? `已打开 ${reply.open_count}次` : '未打开' }}
            </span>
          </div>
          <div v-if="reply.open_count > 0" class="px-2.5 py-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div>
              <span class="text-gray-400">设备</span>
              <div class="font-medium text-gray-800 mt-0.5">
                {{ getDeviceIcon(reply.device_type) }} {{ getDeviceLabel(reply.device_type) || '—' }}
              </div>
            </div>
            <div>
              <span class="text-gray-400">IP</span>
              <div class="font-medium text-gray-800 mt-0.5 truncate" :title="reply.ip_address || ''">
                {{ reply.ip_address || '—' }}
              </div>
            </div>
            <div>
              <span class="text-gray-400">地区</span>
              <div class="font-medium text-gray-800 mt-0.5 truncate" :title="formatReplyLocation(reply)">
                {{ formatReplyLocation(reply) || '—' }}
              </div>
            </div>
            <div>
              <span class="text-gray-400">打开时间</span>
              <div class="font-medium text-gray-700 mt-0.5">
                {{ reply.first_opened_at ? formatDateTime(reply.first_opened_at) : '—' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  email: {
    type: Object,
    required: true
  }
})

const hasTracking = computed(() => (props.email?.outbound_reply_count || 0) > 0)

const openInfo = computed(() => props.email?.outbound_open_info || {})

const hasOpenInfo = computed(() => {
  const info = openInfo.value
  return info.device_type || info.ip_address || info.location || info.region || info.city || info.country
})

const replies = computed(() => props.email?.outbound_replies || [])

const badgeClass = computed(() => {
  if (props.email?.outbound_is_opened) return 'bg-purple-200 text-purple-900'
  return 'bg-gray-200 text-gray-600'
})

const badgeLabel = computed(() => {
  if (!props.email) return ''
  if (props.email.outbound_is_opened) {
    const count = props.email.outbound_open_count || 0
    return count > 1 ? `已打开 ${count}次` : '已打开'
  }
  return '未打开'
})

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
}

const getDeviceLabel = (deviceType) => {
  const map = { desktop: '电脑', mobile: '手机', tablet: '平板' }
  return map[deviceType] || deviceType || ''
}

const getDeviceIcon = (deviceType) => {
  if (deviceType === 'mobile') return '📱'
  if (deviceType === 'tablet') return '📱'
  if (deviceType === 'desktop') return '💻'
  return '📧'
}

const formatLocation = (info) => {
  if (info?.location) return info.location
  const parts = [info?.city, info?.region, info?.country].filter(Boolean)
  return parts.join(', ')
}

const formatReplyLocation = (reply) => {
  if (reply?.location_display) return reply.location_display
  const parts = [reply?.city, reply?.region, reply?.country].filter(Boolean)
  return parts.join(', ')
}
</script>
